import { createContext } from 'react';

// Type-only: a value import here would create a runtime cycle through
// @shared/lib/hooks' barrel, which re-exports useNavbarIconUrl, and that
// hook imports AppContext from this very file.
import type {
	ColorScheme,
	ColorSchemePreference,
	DeviceInformation,
} from '@shared/lib/hooks';
import { Db, KV, InMemoryKV } from '@shared/storage';
import { BeforeInstallPromptEvent } from '@/constants';

export const AppContext = createContext<AppContextProps>({
	appKey: '',
	lang: '',
	languages: [],
	onLanguageChange: () => {},
	onlineStatus: true,
	device: null,
	isPrinting: false,
	isInstalled: false,
	isSupportInstallPrompt: typeof window.onbeforeinstallprompt === 'object',
	isTouchable: window.matchMedia('(pointer: coarse)').matches,
	prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)')
		.matches,
	kv: new InMemoryKV(),
	baseUrl: '',
	parseGraphQLClientError: () => new Error(''),
	colorScheme: 'light',
	colorSchemePreference: 'light',
	setColorSchemePreference: () => {},
});

interface AppContextProps {
	appKey: string;
	lang: string;
	device: DeviceInformation | null;
	languages: Array<string>;
	onLanguageChange: Function;
	isTouchable: boolean;
	/**
	 * Whether motion is reduced: the reader's Settings choice, or the OS
	 * "Reduce Motion" / "Remove animations" setting when they left it on
	 * "system" (see useReducedMotion, @shared/lib/hooks), kept live -- flips
	 * without a reload when either changes. For animations `motion` doesn't run
	 * (Swiper, animate.css, hand-written CSS/JS); `motion` components already
	 * honour it through <MotionConfig> in app/app.tsx.
	 */
	prefersReducedMotion: boolean;
	onlineStatus: boolean;
	isPrinting: boolean;
	db?: Db;
	isInstalled: boolean;
	isSupportInstallPrompt: boolean;
	deferredPrompt?: BeforeInstallPromptEvent;
	kv: KV;
	baseUrl: string;
	parseGraphQLClientError: (err: unknown) => Error;
	/** The app's current light/dark mode -- see useColorScheme (@shared/lib/hooks). */
	colorScheme: ColorScheme;
	/** The reader's raw choice ('system' | 'light' | 'dark'), e.g. for a settings control to show which is selected. */
	colorSchemePreference: ColorSchemePreference;
	/** Persists the reader's choice and applies it immediately. Called by the Settings dialog's Theme control. */
	setColorSchemePreference: (preference: ColorSchemePreference) => void;
}
